import { logger } from '#pplx/frontend/logger/browser.js';
export const loadSpaceProjectThreadsGraphQLClient = () => import('#pplx/frontend/apps/ask/spaces-redesign/graphql/spaceProjectThreadsGraphQLClient.js');
/**
 * Matches the subscription's first-page size so live snapshots replace exactly
 * the head the initial query loaded, and so a prefetched page is
 * indistinguishable from one the page loaded itself.
 */
export const PROJECT_THREADS_PAGE_SIZE = 25;
// Committed tab scopes kept for instant replay on return (3 tabs across a
// couple of recently visited spaces).
const SCOPE_CACHE_LIMIT = 6;
// Searched scopes are keyed but never cached for replay: debounced typing
// would churn per-term entries through the small LRU and evict the tab
// scopes it exists for.
const SEARCH_SCOPE_KEY_MARKER = ':q=';
/**
 * The cache-identity segment for the signed-in session. Owned here so every
 * consumer keys entries the same way — rows never replay for another account.
 */
export function projectThreadsSessionKey(session) {
    return session?.user?.email ?? '';
}
/**
 * Identifies the list scope a row set belongs to: the signed-in session, one
 * space, one session tab, and (for searched views) the term. Empty whenever
 * the scope is not servable — disabled, no session, or no space — so those
 * states never read as a loaded scope; the empty key is the single disabled
 * signal for every consumer.
 */
export function projectThreadsScopeKey({ enabled, sessionKey, spaceUuid, ownThreadsOnly, archivedOnly, searchTerm, }) {
    if (!enabled || !sessionKey || !spaceUuid)
        return '';
    const base = [
        sessionKey,
        spaceUuid,
        ownThreadsOnly ? 'mine' : 'all',
        archivedOnly ? 'archived' : 'active',
    ].join(':');
    return searchTerm ? `${base}${SEARCH_SCOPE_KEY_MARKER}${searchTerm}` : base;
}
/**
 * The first-page query arguments for a scope. Owned beside the scope key so
 * the request and the key its response is stored under can never disagree.
 */
export function projectThreadsFirstPageRequest({ spaceUuid, ownThreadsOnly, archivedOnly, searchTerm, }) {
    return {
        spaceUuid,
        ownThreadsOnly,
        searchTerm,
        groupType: archivedOnly ? 'ARCHIVED' : 'ALL',
        count: PROJECT_THREADS_PAGE_SIZE,
    };
}
/** A fetched first page as the loaded scope state every adopter uses. */
export function pageToScopeState(page, scopeKey) {
    return {
        status: 'active',
        rows: page.rows,
        cursor: page.endCursor,
        hasNextPage: page.hasNextPage,
        headIDs: undefined,
        scopeKey,
    };
}
// Module-level so the sidebar's hover prefetch warms the same cache the page
// mounts with, and so committed scopes replay across page mounts the way the
// REST path's query cache did. Replayed rows reconcile against fresh server
// data on mount, so staleness is bounded by that reconcile.
const scopeCache = new Map();
const storedAtByScopeKey = new Map();
export function getProjectThreadsScopeCacheEntry(scopeKey) {
    return scopeCache.get(scopeKey);
}
/**
 * True while the entry was written within maxAgeMs. A fresh entry lets the
 * page skip its mount reconcile fetch when the live subscription will
 * reconcile the head anyway.
 */
export function isProjectThreadsScopeCacheEntryFresh(scopeKey, maxAgeMs) {
    const storedAt = storedAtByScopeKey.get(scopeKey);
    return storedAt !== undefined && Date.now() - storedAt <= maxAgeMs;
}
export function deleteProjectThreadsScopeCacheEntry(scopeKey) {
    scopeCache.delete(scopeKey);
    storedAtByScopeKey.delete(scopeKey);
}
/**
 * Persists a loaded scope for replay. Only committed non-search scopes with a
 * live head are kept; re-storing refreshes the scope's LRU position. A
 * paginated tail is truncated at store time — replay serves the first page
 * and reconciles on mount, so retaining a deep tail for the page lifetime
 * buys nothing — and the pagination boundary is dropped with it (the
 * reconcile delivers a fresh one).
 */
export function storeProjectThreadsScopeCacheEntry(state, { fromPrefetch = false } = {}) {
    if (state.status !== 'active' ||
        state.headStale ||
        !state.scopeKey ||
        state.scopeKey.includes(SEARCH_SCOPE_KEY_MARKER)) {
        return;
    }
    // A hover prefetch is best-effort and must not evict committed tab scopes
    // when the cache is full.
    if (fromPrefetch && !scopeCache.has(state.scopeKey) && scopeCache.size >= SCOPE_CACHE_LIMIT) {
        return;
    }
    const entry = state.rows !== null && state.rows.length > PROJECT_THREADS_PAGE_SIZE
        ? {
            ...state,
            rows: state.rows.slice(0, PROJECT_THREADS_PAGE_SIZE),
            cursor: null,
            hasNextPage: true,
            headIDs: undefined,
        }
        : state;
    scopeCache.delete(entry.scopeKey);
    scopeCache.set(entry.scopeKey, entry);
    storedAtByScopeKey.set(entry.scopeKey, Date.now());
    while (scopeCache.size > SCOPE_CACHE_LIMIT) {
        const oldest = scopeCache.keys().next().value;
        if (oldest === undefined) {
            break;
        }
        deleteProjectThreadsScopeCacheEntry(oldest);
    }
}
/**
 * Warms the default all-members scope from navigation intent (sidebar hover)
 * so the project page paints instantly from the scope cache and reconciles
 * live, without touching the legacy REST list. Best-effort: cached scopes are
 * skipped, failures only log, and no lifecycle metrics are emitted so
 * page-load telemetry stays untouched. A hover racing the page mount costs no
 * extra network request — Relay dedupes the identical in-flight operation on
 * the shared environment.
 */
export function prefetchSpaceProjectThreads({ spaceUuid, sessionKey, }) {
    const scopeKey = projectThreadsScopeKey({
        enabled: true,
        sessionKey,
        spaceUuid,
        ownThreadsOnly: false,
        archivedOnly: false,
        searchTerm: '',
    });
    if (!scopeKey || scopeCache.has(scopeKey)) {
        return;
    }
    loadSpaceProjectThreadsGraphQLClient()
        .then((client) => client.fetchSpaceProjectThreadsPage(projectThreadsFirstPageRequest({
        spaceUuid,
        ownThreadsOnly: false,
        archivedOnly: false,
        searchTerm: '',
    })))
        .then((page) => {
        if (page) {
            storeProjectThreadsScopeCacheEntry(pageToScopeState(page, scopeKey), {
                fromPrefetch: true,
            });
        }
    })
        .catch((error) => {
        logger.warn('Space project threads prefetch failed.', error);
    });
}
//# sourceMappingURL=spaceProjectThreadsScopeCache.js.map